import React from "react";
import { Box, ToggleButtonGroup, ToggleButton } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

interface BooleanToggleProps {
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  isChanged?: boolean;
  trueLabel?: string;
  falseLabel?: string;
  containerSx?: SxProps<Theme>;
  groupSx?: SxProps<Theme>;
  trueButtonSx?: SxProps<Theme>;
  falseButtonSx?: SxProps<Theme>;
}

const mergeSx = (base: SxProps<Theme>, override?: SxProps<Theme>): SxProps<Theme> => {
  if (!override) {
    return base;
  }

  return Array.isArray(override)
    ? [base, ...override]
    : [base, override];
};

export const BooleanToggle: React.FC<BooleanToggleProps> = ({
  value,
  onChange,
  disabled = false,
  isChanged = false,
  trueLabel = "True",
  falseLabel = "False",
  containerSx,
  groupSx,
  trueButtonSx,
  falseButtonSx,
}) => {
  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    selection: "true" | "false" | null
  ) => {
    if (selection === null) return;
    onChange(selection === "true");
  };

  const baseContainerSx: SxProps<Theme> = {
    maxWidth: "50%",
    marginLeft: "auto",
    display: "flex",
    justifyContent: "flex-end",
  };
  const baseGroupSx: SxProps<Theme> = {
    backgroundColor: isChanged
      ? "rgba(25, 118, 210, 0.08)"
      : "transparent",
    borderRadius: "10px",
    padding: "4px",
    gap: "6px",
  };
  const baseTrueButtonSx: SxProps<Theme> = {
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 500,
    padding: "6px 16px",
    borderRadius: "8px !important",
    border: "1px solid #d0d0d0",
    color: disabled ? "text.disabled" : "text.primary",
    "&.Mui-selected": {
      backgroundColor: "#1976d2",
      color: "#fff",
      borderColor: "#1976d2",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "#1565c0",
    },
    "&:not(.Mui-selected)": {
      backgroundColor: "#fff",
    },
  };
  const baseFalseButtonSx: SxProps<Theme> = {
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 500,
    padding: "6px 16px",
    borderRadius: "8px !important",
    border: "1px solid #d0d0d0",
    color: disabled ? "text.disabled" : "text.primary",
    "&.Mui-selected": {
      backgroundColor: "#1976d2",
      color: "#fff",
      borderColor: "#1976d2",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "#1565c0",
    },
    "&:not(.Mui-selected)": {
      backgroundColor: "#fff",
    },
  };

  const resolvedContainerSx = mergeSx(baseContainerSx, containerSx);
  const resolvedGroupSx = mergeSx(baseGroupSx, groupSx);
  const resolvedTrueButtonSx = mergeSx(baseTrueButtonSx, trueButtonSx);
  const resolvedFalseButtonSx = mergeSx(baseFalseButtonSx, falseButtonSx);

  return (
    <Box
      sx={resolvedContainerSx}
    >
      <ToggleButtonGroup
        exclusive
        value={value ? "true" : "false"}
        onChange={handleChange}
        disabled={disabled}
        sx={resolvedGroupSx}
      >
        <ToggleButton
          value="true"
          disableRipple
          sx={resolvedTrueButtonSx}
        >
          {trueLabel}
        </ToggleButton>
        <ToggleButton
          value="false"
          disableRipple
          sx={resolvedFalseButtonSx}
        >
          {falseLabel}
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};
